import { resolvePriceMatrixPrice } from '@/lib/pricing/price-matrix';

/**
 * Vrij aantal voor CUSTOM-producten (vooral Probo).
 *
 * Probo-producten hebben een `copies`-optie met staffels (1, 2, 5, 10 ...) en
 * meestal `quantitySettings.type = 'fixed'`. Tot 13 okt 2026 betekende dat
 * dat een klant alleen die staffels kon kiezen: de server weigerde elk ander
 * aantal ("Ongeldige optie: copies=7") en het invoerveld "Eigen aantal"
 * verdween of liet het aantal stil weer vallen.
 *
 * Een ander aantal is veilig te prijzen zolang de prijs:
 * - lineair met het aantal meeloopt (oppervlakteprijs of een matrix zonder
 *   `copies` in de sleutel en zonder orderTotaal), of
 * - uit een matrix met `copies` in de sleutel komt EN het aantal tussen twee
 *   gemeten staffels ligt. Dan rekenen we het hoogste stuktarief van de twee
 *   buren, en nooit minder dan het totaal van de lagere staffel. Probo's
 *   stukprijs is niet altijd dalend (paalbanner, onderzetters), dus het
 *   stuktarief van alleen de lagere staffel zou te laag kunnen uitvallen.
 *   Boven de hoogste gemeten staffel geldt het stuktarief van die staffel: de
 *   staffel houdt daar op, het bestelbare aantal niet (besluit Gert 14-10-2026).
 *
 * Deze module wordt door de server (calculateCustomProductPrice) en de
 * configurator gedeeld, zodat beide hetzelfde aantal accepteren en dezelfde
 * prijs uitrekenen.
 */

type OptionValueLike = { value?: unknown; priceModifier?: unknown; type?: string };
type OptionLike = { id?: string; name?: string; type?: string; values?: OptionValueLike[]; min?: number; max?: number };
type QuantitySettingsLike = {
  type?: string;
  values?: unknown[];
  minimum?: unknown;
  maximum?: unknown;
  min?: unknown;
  max?: unknown;
  step?: unknown;
};
type PriceMatrixLike = {
  enabled?: boolean;
  keyFields?: unknown;
  priceIsOrderTotal?: boolean;
  [key: string]: unknown;
};
type SpecificationsLike = {
  quantitySettings?: QuantitySettingsLike | null;
  priceMatrix?: PriceMatrixLike | null;
  livePrintComPricing?: unknown;
  [key: string]: unknown;
};

export type OpenCustomQuantityRange = {
  minimum: number;
  maximum: number;
  step: number;
  presets: number[];
};

const COPIES = 'copies';
/** Bovengrens als technische vangrail; een staffel is geen maximum. */
export const OPEN_QUANTITY_MAXIMUM = 100000;

function positiveIntegers(values: unknown[] | undefined): number[] {
  return [...new Set((values || []).map(Number).filter((value) => Number.isSafeInteger(value) && value > 0))]
    .sort((a, b) => a - b);
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

function positiveInteger(value: unknown): number | null {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.floor(number) : null;
}

function findCopiesOption(options: OptionLike[] | undefined | null): OptionLike | undefined {
  return (options || []).find((option) => option?.id === COPIES || option?.name === COPIES);
}

/** True als een prijsstructuur ergens een voorwaarde of sleutel op `copies` heeft. */
function referencesCopies(value: unknown, depth = 0): boolean {
  if (!value || typeof value !== 'object' || depth > 8) return false;
  if (Array.isArray(value)) return value.some((item) => referencesCopies(item, depth + 1));
  return Object.entries(value as Record<string, unknown>).some(([key, item]) =>
    key === COPIES || referencesCopies(item, depth + 1));
}

function matrixKeyFields(priceMatrix: PriceMatrixLike | null | undefined): string[] {
  return Array.isArray(priceMatrix?.keyFields) ? priceMatrix!.keyFields.map(String) : [];
}

/**
 * Het bereik waarbinnen een klant zelf een aantal mag kiezen, of null als het
 * product alleen de vaste staffels toestaat.
 */
export function getOpenCustomQuantityRange(
  options: OptionLike[] | undefined | null,
  specifications: SpecificationsLike | undefined | null,
): OpenCustomQuantityRange | null {
  const copiesOption = findCopiesOption(options);
  if (!copiesOption || !Array.isArray(copiesOption.values) || copiesOption.values.length === 0) return null;
  if (specifications?.livePrintComPricing) return null;
  // Een staffel met een eigen toeslag kan niet tussen twee staffels in
  // geprijsd worden zonder die toeslag te verliezen.
  if (copiesOption.values.some((value) => Number(value?.priceModifier || 0) !== 0)) return null;

  const settings = specifications?.quantitySettings || undefined;
  const presets = positiveIntegers([
    ...copiesOption.values.map((value) => value?.value),
    ...(Array.isArray(settings?.values) ? settings!.values : []),
  ]);
  if (presets.length === 0) return null;

  const priceMatrix = specifications?.priceMatrix?.enabled === true ? specifications.priceMatrix : null;
  const keyedOnCopies = matrixKeyFields(priceMatrix).includes(COPIES);
  // Een ordertotaal dat niet van het aantal afhangt, zou bij elk ander aantal
  // dezelfde prijs geven.
  if (priceMatrix && !keyedOnCopies && priceMatrix.priceIsOrderTotal === true) return null;
  // Oppervlakte- of stukformules met varianten per aantal (banier, akoestisch
  // textielframe) vallen bij een ander aantal op een verkeerde variant terug.
  if (referencesCopies(specifications?.areaPricing) || referencesCopies(specifications?.pieceFormulaPricing)) return null;

  const explicitStep = positiveInteger(settings?.step);
  const step = explicitStep ?? presets.reduce((acc, value) => gcd(acc, value));
  // Minimale afname van de leverancier (specifications.minimumQuantity) gaat voor.
  const minimum = Math.max(
    positiveInteger(settings?.minimum ?? settings?.min) ?? presets[0],
    positiveInteger(specifications?.minimumQuantity) ?? 1,
  );
  const explicitMaximum = positiveInteger(settings?.maximum ?? settings?.max) ?? positiveInteger(copiesOption.max);
  const largestPreset = presets[presets.length - 1];
  // Met copies in de matrixsleutel loopt het aantal door voorbij de hoogste
  // staffel, tegen het stuktarief van die staffel. Een maximum gelijk aan de
  // hoogste staffel is geen echte grens; alleen een hoger maximum telt.
  // Oppervlakteprijzen met staffels op totale oppervlakte kunnen voorbij de
  // laatste staffel terugvallen op een minimumprijs; daar blijft de grens.
  const maximum = keyedOnCopies
    ? (explicitMaximum && explicitMaximum > largestPreset ? explicitMaximum : OPEN_QUANTITY_MAXIMUM)
    : explicitMaximum ?? largestPreset;

  if (!(maximum > minimum)) return null;
  return { minimum, maximum, step, presets };
}

export function isOpenCustomQuantityAllowed(quantity: number, range: OpenCustomQuantityRange | null): boolean {
  if (!range || !Number.isSafeInteger(quantity)) return false;
  if (range.presets.includes(quantity)) return true;
  return quantity >= range.minimum && quantity <= range.maximum && quantity % range.step === 0;
}

/**
 * Geeft de specificaties terug met een open aantal-instelling
 * (`preset-plus-custom`) als het product een vrij aantal toelaat. De
 * configurator leest op veel plekken `quantitySettings`; door het hier één
 * keer te normaliseren gedragen al die plekken zich hetzelfde.
 */
export function withOpenCustomQuantitySettings<T extends SpecificationsLike | null | undefined>(
  providerType: string | undefined,
  options: OptionLike[] | undefined | null,
  specifications: T,
): T {
  if (providerType !== 'CUSTOM' || !specifications) return specifications;
  const range = getOpenCustomQuantityRange(options, specifications);
  if (!range) return specifications;
  return {
    ...specifications,
    quantitySettings: {
      ...(specifications.quantitySettings || {}),
      type: 'preset-plus-custom',
      values: range.presets,
      minimum: range.minimum,
      maximum: range.maximum,
      step: range.step,
    },
  };
}

/**
 * Matrixprijs voor een configuratie, ook als `copies` tussen twee gemeten
 * staffels valt. Geeft null als er geen veilige prijs is.
 *
 * Het resultaat heeft dezelfde betekenis als een matrixcel: een ordertotaal
 * als `priceIsOrderTotal`, anders een stukprijs.
 */
export function resolveCustomMatrixPrice(
  priceMatrix: PriceMatrixLike,
  countryCode: string | null | undefined,
  configuration: Record<string, unknown>,
): number | null {
  const keyFields = matrixKeyFields(priceMatrix);
  const keyFor = (config: Record<string, unknown>) => keyFields.map((field) => String(config[field] ?? '')).join('|');
  const exact = Number(resolvePriceMatrixPrice(priceMatrix as any, countryCode, keyFor(configuration)));
  if (Number.isFinite(exact) && exact > 0) return exact;
  if (!keyFields.includes(COPIES)) return null;

  const quantity = Number(configuration[COPIES]);
  if (!Number.isSafeInteger(quantity) || quantity <= 0) return null;

  // Alle gemeten staffels voor precies deze uitvoering verzamelen.
  const copiesIndex = keyFields.indexOf(COPIES);
  const wanted = keyFields.map((field, index) => (index === copiesIndex ? null : String(configuration[field] ?? '')));
  const tiers: Array<{ quantity: number; price: number }> = [];
  const keys = new Set<string>([
    ...Object.keys((priceMatrix as any).prices || {}),
    ...Object.values((priceMatrix as any).pricesByCountry || {}).flatMap((matrix: any) => Object.keys(matrix || {})),
    ...(Array.isArray((priceMatrix as any).priceKeys) ? (priceMatrix as any).priceKeys : []),
  ]);
  for (const key of keys) {
    const parts = String(key).split('|');
    if (parts.length !== keyFields.length) continue;
    if (!wanted.every((value, index) => value === null || value === parts[index])) continue;
    const tierQuantity = Number(parts[copiesIndex]);
    if (!Number.isSafeInteger(tierQuantity) || tierQuantity <= 0) continue;
    const price = Number(resolvePriceMatrixPrice(priceMatrix as any, countryCode, key));
    if (Number.isFinite(price) && price > 0) tiers.push({ quantity: tierQuantity, price });
  }
  const lower = tiers.filter((tier) => tier.quantity < quantity).sort((a, b) => b.quantity - a.quantity)[0];
  const upper = tiers.filter((tier) => tier.quantity > quantity).sort((a, b) => a.quantity - b.quantity)[0];
  if (!lower) return null;
  if (!upper) {
    // Boven de hoogste staffel: het stuktarief van die staffel.
    const topTotal = priceMatrix.priceIsOrderTotal === true ? lower.price : lower.price * lower.quantity;
    const total = Math.ceil((topTotal / lower.quantity) * quantity * 100) / 100;
    return priceMatrix.priceIsOrderTotal === true ? total : total / quantity;
  }

  // Hoogste stuktarief van de twee buren, maar nooit duurder dan de volgende
  // staffel in zijn geheel (anders kost 7 meer dan 10) en nooit goedkoper dan
  // de vorige staffel. Een groter aantal kost bij Probo in totaal nooit
  // minder, dus het totaal van de hogere staffel dekt ook de kostprijs van dit
  // aantal.
  const lowerTotal = priceMatrix.priceIsOrderTotal === true ? lower.price : lower.price * lower.quantity;
  const upperTotal = priceMatrix.priceIsOrderTotal === true ? upper.price : upper.price * upper.quantity;
  const unit = Math.max(lowerTotal / lower.quantity, upperTotal / upper.quantity);
  const total = Math.max(lowerTotal, Math.min(Math.ceil(unit * quantity * 100) / 100, upperTotal));
  return priceMatrix.priceIsOrderTotal === true ? total : total / quantity;
}
